'use client';

import { useEffect, useRef } from 'react';
import useAccount from '@/lib/hooks/useAccount';
import { initInsights, setAuthenticatedUser } from '@/lib/algolia';
import { useAlgoliaKey } from '@/providers/algolia-key';

// EWM-Algolia Insights PR-A (foundation).
//
// Mount this hook once per session at a top-level provider so:
//   1. `initInsights()` runs as soon as the app boots — binds the
//      anonymous userToken from the `_at_token` cookie (set by edge
//      middleware) to the search-insights singleton.
//   2. When the dealer logs in, `setAuthenticatedUserToken` is called
//      with `account.accountId`. Algolia stitches the anon and
//      authenticated journeys server-side; we never reset the anon
//      userToken because the documented pattern is "stitched journey
//      survives logout".
//
// Idempotent — re-mounts during dev hot-reload are safe (initInsights
// short-circuits on the singleton).
//
// PR-B will use the `aa(...)` events bound by this init when wiring
// clickedObjectIDsAfterSearch / viewedObjectIDs / convertedObjectIDsAfterSearch
// on the autocomplete + search + PLP entry points.
const useInsights = (): void => {
  const { account } = useAccount();
  const { securedApiKey } = useAlgoliaKey();
  const stitchedRef = useRef<string | undefined>(undefined);

  // Init once the secured key is available (SSR-injected, so present on
  // first client render). Insights uses the scoped key — never the raw one.
  useEffect(() => {
    if (securedApiKey) initInsights(securedApiKey);
  }, [securedApiKey]);

  useEffect(() => {
    const accountId = account?.accountId;
    if (!accountId) return;
    if (stitchedRef.current === accountId) return;
    setAuthenticatedUser(accountId);
    stitchedRef.current = accountId;
  }, [account?.accountId]);
};

export default useInsights;
